import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuth, selectPortalBase } from './store/slices/authSlice';

// Auth
import LoginPage from './pages/auth/LoginPage';

// Platform pages  (PLATFORM_* roles plus CLIENT_VENDOR_ADMIN)
import CompanyDashboard  from './pages/company/CompanyDashboard';
import FinishedGoodsList from './pages/company/FinishedGoodsList';
import FinishedGoodDetail from './pages/company/FinishedGoodDetail';
import UserManagement    from './pages/company/UserManagement';
import CompanyReview     from './pages/company/CompanyReview';
import AssignmentBomView from './pages/company/AssignmentBomView';
import CompanyManagement from './pages/company/CompanyManagement';
import InquiriesPage     from './pages/company/InquiriesPage';

// Vendor pages  (VENDOR_ADMIN | VENDOR_USER | VENDOR_VIEWER)
import VendorDashboard   from './pages/vendor/VendorDashboard';
import VendorAssignments from './pages/vendor/VendorAssignments';
import PartArticleForm   from './pages/vendor/PartArticleForm';

// Client pages  (CLIENT_ADMIN | CLIENT_USER | CLIENT_VIEWER)
import ClientDashboard from './pages/client/ClientDashboard';
import ClientRequests  from './pages/client/ClientRequests';
import ClientReview    from './pages/client/ClientReview';

// Shared pages (visible to specific roles across portals — guarded inside component)
import BobsDashboard from './pages/shared/BobsDashboard';

// Layout & guards
import AppLayout      from './components/layout/AppLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import GlobalLoader   from './components/common/GlobalLoader';

export default function App() {
  return (
    <BrowserRouter>
      <GlobalLoader />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* ── Platform portal  (all PLATFORM_* roles) ─────────────────── */}
        <Route path="/platform" element={
          <ProtectedRoute allowedGroups={['platform']}>
            <AppLayout portal="platform" />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"            element={<CompanyDashboard />} />
          <Route path="finished-goods"       element={<FinishedGoodsList />} />
          <Route path="finished-goods/:id"   element={<FinishedGoodDetail />} />
          <Route path="review"               element={<CompanyReview />} />
          <Route path="assignments/:id"      element={<AssignmentBomView />} />
          {/* Compatibility for older CVA bookmarks; all admins share the full dashboard. */}
          <Route path="cva-dashboard"        element={<Navigate to="/platform/dashboard" replace />} />
          {/* Full platform admins only — guarded inside component */}
          <Route path="companies"            element={<CompanyManagement />} />
          <Route path="users"                element={<UserManagement />} />
          <Route path="inquiries"            element={<InquiriesPage />} />
          {/* Super Admin-equivalent roles only — guarded inside component */}
          <Route path="bobs-dashboard"       element={<BobsDashboard />} />
        </Route>

        {/* ── Vendor portal  (all VENDOR_* roles) ─────────────────────── */}
        <Route path="/vendor" element={
          <ProtectedRoute allowedGroups={['vendor']}>
            <AppLayout portal="vendor" />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"        element={<VendorDashboard />} />
          <Route path="assignments"      element={<VendorAssignments />} />
          <Route path="assignments/:id"  element={<PartArticleForm />} />
          {/* VENDOR_ADMIN only — guarded inside component */}
          <Route path="users"            element={<UserManagement />} />
        </Route>

        {/* ── Client portal  (all CLIENT_* roles) ─────────────────────── */}
        <Route path="/client" element={
          <ProtectedRoute allowedGroups={['client']}>
            <AppLayout portal="client" />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"    element={<ClientDashboard />} />
          <Route path="requests"     element={<ClientRequests />} />
          <Route path="review/:id"   element={<ClientReview />} />
          {/* CLIENT_ADMIN only — guarded inside component */}
          <Route path="users"        element={<UserManagement />} />
          {/* CLIENT_ADMIN / CLIENT_USER / CLIENT_VIEWER only — guarded inside component */}
          <Route path="bobs-dashboard" element={<BobsDashboard />} />
        </Route>

        {/* Backward-compat: old /company/* links → /platform/* */}
        <Route path="/company/*" element={<Navigate to="/platform" replace />} />

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function RootRedirect() {
  const isAuth     = useSelector(selectIsAuth);
  const portalBase = useSelector(selectPortalBase);
  // Authenticated → go straight to the portal dashboard
  if (isAuth && portalBase) {
    return <Navigate to={`${portalBase}/dashboard`} replace />;
  }
  // Not authenticated → hard-navigate to the marketing landing page
  window.location.replace('/landing.html');
  return null;
}
